/* eslint-disable @typescript-eslint/no-unused-vars */
import { z } from 'zod'
import { baseEntitySchema } from './core/entity.js'
import { storageSchema } from './core/interfaces/storage.js'
import type { CoreElement } from './core/slate/slate-element.js'
import { entityTypeNames } from './entity-types.js'

/** Every entity schema by type name, for validation. In its own module so `entitySchema` and `isEntity` don't keep it (GH #2294). */
export { schemas } from './entity-schemas.js'

/** Full Schema that from `baseEntitySchema` that contains type field */
export const entitySchema = baseEntitySchema.extend({
  type: z.enum(entityTypeNames),
})

/** @todo move these out of this file */

/**
 * We have the following challenge. Some of the entities we want to be able to embed
 * in the slate editor (e.g., audio) others not (e.g., folder). So some of them
 * must be compatible with SlateElement, which means:
 *  1) index signature
 *  2) children
 *
 * We don't want to have an index signature on all of the entities for type safety.
 */
export type Entity = z.infer<typeof entitySchema>
const entityTypeSet = new Set<string>(entityTypeNames)
export function isEntity<T>(node: T): node is Entity & T {
  return (
    node != null &&
    typeof node === 'object' &&
    'type' in node &&
    typeof (node as Record<string, unknown>).type === 'string' &&
    entityTypeSet.has((node as Record<string, unknown>).type as string)
  )
}

export type TEntity<T> = Entity & T

/** @todo do we need this? */
export type EntityOrElement = CoreElement | Entity

/**
 * Schema used for eTag calculation. We don't include `storage/versionId` since it's managed out-of-band
 */
export const entityETagSchema = baseEntitySchema
  .extend({ storage: storageSchema.omit({ versionId: true }).optional() })
  .loose()
  // Ignore these fields for eTag calculation
  .transform(data => {
    const { lastEditedBy, version, versionAt, createdAt, updatedAt, ...restProps } = data
    return restProps
  })

export type EntityETag = z.infer<typeof entityETagSchema>
